import { FieldValues, FormProvider, useForm, UseFormRegister } from 'react-hook-form';
import { useEffect, useMemo, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ButtonVariantEnum,
  JxtButton,
  JxtModal,
  dateNotInPastSuperRefinerFactory,
  startBeforeEndSuperRefinerFactory,
  ButtonSizeEnum,
  JxtAlertTypeEnum,
  JxtAlert,
  JxtLoader,
  generateToast,
} from '@jooxter/ui';
import { useInvalidateFloorExceptionalClosingHoursQueries } from '../../../queries';
import { LocationCompressed, FloorExceptionalClosingHoursResponse } from '@jooxter/api';
import { useTranslation } from 'react-i18next';
import {
  createGTMGAEvent,
  fromISO,
  getDateTimeFromDateTimeAndStandardTime,
  IExceptionalOpeningHoursForm,
  IRange,
  ISOToLocalDate,
  ISOToLocalTime,
  isSameDay,
} from '@jooxter/utils';
import { useButtonStatus, useFloorExceptionalClosingHoursActions } from '../../../hooks';
import { useOffCanvasStore } from '../../../store';
import { IFloorExceptionalClosingHoursFormPreset } from '../types/floor-exceptional-closing-hours-form.types';
import { useFloorExceptionalClosingHoursFormDefaultValues } from './useFloorExceptionalClosingHoursFormDefaultValues';
import JxtFloorInfos from '@jooxter/ui/src/components/JxtFloorInfos';
import { useGenerateFloorExceptionalClosingHoursForm } from './useGenerateFloorExceptionalClosingHoursForm';
import { useIFloorExceptionalClosingHoursFormToFloorExceptionalClosingHoursAdapter } from '../../../adapters/floor-exceptional-closing-hours/useIFloorExceptionalClosingFormToFloorExceptionalClosingHoursAdapter';
import { useFloorExceptionalClosingHoursSchema } from '../schemas/useFloorExceptionalClosingHoursSchema';
import { AxiosError } from 'axios';
import { useFloorExceptionalClosingHoursFormContext } from './useFloorExceptionalClosingHoursFormContext';
import { useFetchFloor } from '@jooxter/floors';

export const useFloorExceptionalClosingHoursForm = (
  selectedBuilding: LocationCompressed,
  floorExceptionalClosingHoursToEdit?: FloorExceptionalClosingHoursResponse | null,
  floorExceptionalClosingHoursFormPreset?: IFloorExceptionalClosingHoursFormPreset
) => {
  // @TODO : see when react-hook-form properly supports react-compiler
  // https://github.com/orgs/react-hook-form/discussions/12524
  // https://jooxter.atlassian.net/browse/ND-6018
  'use no memo';
  const [showConfirmFloorClosureModal, setShowConfirmFloorClosureModal] = useState(false);
  const { t } = useTranslation();
  const { children, setLoading, loading } = useButtonStatus();
  const { floor: floorExceptionalClosingHoursToEditFloor } = useFetchFloor(floorExceptionalClosingHoursToEdit?.floorId);
  const floor = floorExceptionalClosingHoursFormPreset
    ? floorExceptionalClosingHoursFormPreset.floor
    : floorExceptionalClosingHoursToEditFloor;
  const floorExceptionalClosingHoursFormDefaultValues = useFloorExceptionalClosingHoursFormDefaultValues(
    floorExceptionalClosingHoursFormPreset,
    floorExceptionalClosingHoursToEdit,
    floor,
    selectedBuilding
  );
  const schema = useFloorExceptionalClosingHoursSchema(floorExceptionalClosingHoursFormDefaultValues);
  const [alert, setAlert] = useState<React.ReactElement>();
  const [bookingRange, setBookingRange] = useState<IRange>();
  const [additionalBookingRange, setAdditionalBookingRange] = useState<IRange>();
  const [isFirstRender, setIsFirstRender] = useState<boolean>(true);
  const [floorExceptionalClosingHoursFormToFloorExceptionalClosingHoursAdapter] =
    useIFloorExceptionalClosingHoursFormToFloorExceptionalClosingHoursAdapter();
  const close = useOffCanvasStore((state) => state.close);
  const { invalidateFloorExceptionalClosingHoursQueries } = useInvalidateFloorExceptionalClosingHoursQueries();

  const schemaRefined = useMemo(() => {
    return schema
      .superRefine(
        dateNotInPastSuperRefinerFactory(
          'manage-space-form.error.date-not-in-past',
          selectedBuilding?.timezone,
          !!floorExceptionalClosingHoursToEdit
        )
      )
      .superRefine(startBeforeEndSuperRefinerFactory('manage-space-form.error.compare-date'));
  }, [schema]);

  const form = useForm<IExceptionalOpeningHoursForm>({
    resolver: zodResolver(schemaRefined),
    defaultValues: floorExceptionalClosingHoursFormDefaultValues,
    mode: 'all',
  });

  const { register, reset, handleSubmit, formState, getFieldState, watch } = form;

  const shouldDisableSubmitButton = !formState.isValid;
  const watchStart = watch('start');
  const watchEnd = watch('end');

  const { create, update, filteredBookings } = useFloorExceptionalClosingHoursActions({
    floorId: floor?.id,
    range: bookingRange,
    additionalRange: additionalBookingRange,
  });
  const floorExceptionalClosingHoursFormContext = useFloorExceptionalClosingHoursFormContext();

  useEffect(() => {
    // https://react-hook-form.com/docs/useform#defaultValues
    // defaultValues are cached. To reset them, use the reset API.
    reset(floorExceptionalClosingHoursFormDefaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [floorExceptionalClosingHoursFormDefaultValues]);

  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false);
    } else {
      createGTMGAEvent('ManageSpaces', 'Create', 'Floor Exceptional Closing');
    }
  }, [isFirstRender]);

  useEffect(() => {
    setBookingRange({
      from: getDateTimeFromDateTimeAndStandardTime(watchStart.date, watchStart.time),
      to: getDateTimeFromDateTimeAndStandardTime(watchEnd.date, watchEnd.time),
    });
    setAdditionalBookingRange(undefined);
  }, [watchStart, watchEnd]);

  const generatedForm = useGenerateFloorExceptionalClosingHoursForm(
    {
      register: register as unknown as UseFormRegister<FieldValues>,
      formState,
      getFieldState,
    },
    {
      schema,
      floorExceptionalClosingHoursToEdit,
      floor,
    },
    selectedBuilding
  );

  const onSuccessUpdate = (data: FloorExceptionalClosingHoursResponse) => {
    const floorExceptionalClosingHours = data;
    if (!floorExceptionalClosingHours.id) {
      return;
    }
    const uniqueDay = isSameDay(
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      fromISO(floorExceptionalClosingHours.timestampFrom!),
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      fromISO(floorExceptionalClosingHours.timestampTo!)
    );
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const startDate = ISOToLocalDate(floorExceptionalClosingHours.timestampFrom!, selectedBuilding?.timezone);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const endDate = ISOToLocalDate(floorExceptionalClosingHours.timestampTo!, selectedBuilding?.timezone);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const startTime = ISOToLocalTime(floorExceptionalClosingHours.timestampFrom!, selectedBuilding?.timezone);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const endTime = ISOToLocalTime(floorExceptionalClosingHours.timestampTo!, selectedBuilding?.timezone);
    invalidateFloorExceptionalClosingHoursQueries([floorExceptionalClosingHours.id]);
    close();
    generateToast(
      t<string>('exceptional-closing-updated'),
      false,
      uniqueDay
        ? t<string>('exceptional-closing-updated-message', {
            resourceName: floor?.name,
            date: startDate,
            startTime,
            endTime,
          })
        : t<string>('exceptional-closing-updated-message-with-end-date', {
            resourceName: floor?.name,
            startDate,
            endDate,
            startTime,
            endTime,
          })
    );
  };

  const onSuccessCreate = (data: FloorExceptionalClosingHoursResponse) => {
    const floorExceptionalClosingHours = data;
    if (!floorExceptionalClosingHours.id) {
      return;
    }
    const uniqueDay = isSameDay(
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      fromISO(floorExceptionalClosingHours.timestampFrom!),
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      fromISO(floorExceptionalClosingHours.timestampTo!)
    );
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const startDate = ISOToLocalDate(floorExceptionalClosingHours.timestampFrom!, selectedBuilding?.timezone);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const endDate = ISOToLocalDate(floorExceptionalClosingHours.timestampTo!, selectedBuilding?.timezone);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const startTime = ISOToLocalTime(floorExceptionalClosingHours.timestampFrom!, selectedBuilding?.timezone);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const endTime = ISOToLocalTime(floorExceptionalClosingHours.timestampTo!, selectedBuilding?.timezone);
    invalidateFloorExceptionalClosingHoursQueries([floorExceptionalClosingHours.id]);
    close();
    generateToast(
      t<string>('exceptional-closing-created'),
      false,
      uniqueDay
        ? t<string>('exceptional-closing-created-message', {
            resourceName: floor?.name,
            date: startDate,
            startTime,
            endTime,
          })
        : t<string>('exceptional-closing-created-message-with-end-date', {
            resourceName: floor?.name,
            startDate,
            endDate,
            startTime,
            endTime,
          }),
      <JxtButton
        className="p-0!"
        size={ButtonSizeEnum.Large}
        variant={ButtonVariantEnum.Link}
        onClick={() => {
          floorExceptionalClosingHoursFormContext?.showFloorExceptionalClosingHoursDetails(
            floorExceptionalClosingHours
          );
        }}
      >
        {t('open-exceptional-closing')}
      </JxtButton>
    );
  };

  const onError = (error: unknown) => {
    const axiosError = error as AxiosError;
    const response = axiosError.response?.data as { error: string; description: string }[];
    if (response.length > 0) {
      setAlert(<JxtAlert type={JxtAlertTypeEnum.Error} text={response[0].error} closeable />);
    }
  };

  const onSettled = () => {
    setLoading(false);
  };

  const onHideConfirmFloorClosureModal = () => {
    setShowConfirmFloorClosureModal(false);
    setLoading(false);
  };

  const onConfirm = (data: IExceptionalOpeningHoursForm) => {
    setShowConfirmFloorClosureModal(false);
    if (!floor?.id) {
      return;
    }
    setLoading(true);
    setAlert(undefined);
    const result = schema.safeParse(data);
    if (!result.success) {
      return;
    }
    createGTMGAEvent('ManageSpaces', 'Create', 'Floor Create Button');
    const floorExceptionalClosingHours = floorExceptionalClosingHoursFormToFloorExceptionalClosingHoursAdapter(
      data,
      floor?.id
    );
    if (!floorExceptionalClosingHoursToEdit) {
      create(floorExceptionalClosingHours, onSuccessCreate, onError, onSettled);
    } else if (floorExceptionalClosingHoursToEdit?.id) {
      update(
        { id: floorExceptionalClosingHoursToEdit?.id, payload: floorExceptionalClosingHours },
        onSuccessUpdate,
        onError,
        onSettled
      );
    }
  };

  const onSubmit = () => {
    if (filteredBookings.length > 0) {
      setShowConfirmFloorClosureModal(true);
    } else {
      onConfirm(form.getValues());
    }
  };

  if (!floorExceptionalClosingHoursFormDefaultValues || !floor) {
    return [];
  }

  return (
    <FormProvider<IExceptionalOpeningHoursForm> {...form}>
      <form
        key="form"
        className="w-full h-full flex flex-col justify-between overflow-hidden -mt-1 -ml-1"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="relative h-full">
          {loading && (
            <div className="absolute backdrop-blur-xs flex items-center justify-center z-10 h-full w-full">
              <JxtLoader />
            </div>
          )}
          <div
            className={`flex flex-col gap-6 pb-6 px-1 pt-1 overflow-auto grow h-full ${loading ? 'opacity-50 pointer-events-none' : ''}`}
          >
            <JxtFloorInfos
              name={floor.name}
              locationName={selectedBuilding?.name}
              floor={floor.number}
              pictures={selectedBuilding?.pictures?.[0]}
            />
            {alert}
            {generatedForm}
          </div>
        </div>
        <footer className="pt-3 pb-3 max-sm:pb-4 pl-1 border-t border-neutral-20">
          <JxtButton
            variant={loading ? ButtonVariantEnum.OutlinePrimary : ButtonVariantEnum.Primary}
            className="w-full"
            type="submit"
            disabled={loading ? true : shouldDisableSubmitButton}
          >
            {loading
              ? children
              : floorExceptionalClosingHoursToEdit
                ? t('manage-space-form.submit-edit')
                : t('manage-space-form.submit-create')}
          </JxtButton>
        </footer>
      </form>
      <JxtModal
        show={showConfirmFloorClosureModal}
        onHide={onHideConfirmFloorClosureModal}
        header={{ title: t<string>('jooxter.modal.floor.closing.exceptional.title'), icon: 'trash' }}
        footer={
          <>
            <JxtButton onClick={onHideConfirmFloorClosureModal} variant={ButtonVariantEnum.Secondary}>
              {t('cancel')}
            </JxtButton>
            <JxtButton onClick={handleSubmit(onConfirm)} variant={ButtonVariantEnum.Primary}>
              {t('confirm')}
            </JxtButton>
          </>
        }
      >
        {filteredBookings.length} {t<string>('jooxter.modal.floor.closing.exceptional.message')}
        <p className="text-sm italic">{t<string>('floor.action.uncancellable')}</p>
      </JxtModal>
    </FormProvider>
  );
};
